import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Sparkles, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const GuidedTour = ({ isOpen, onClose, viewerRef, modelInfo }) => {
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Start tour when opened
  useEffect(() => {
    if (isOpen && steps.length === 0 && !isLoading) {
      generateTour();
    }
  }, [isOpen]);

  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setSteps([]);
      setCurrentStep(0);
      setError(null);
    }
  }, [isOpen]);

  const captureScreenshot = () => {
    if (viewerRef?.current?.captureScreenshot) {
      try {
        return viewerRef.current.captureScreenshot();
      } catch (e) {
        console.error('Failed to capture screenshot:', e);
      }
    }
    return null;
  };

  const generateTour = async () => {
    setIsLoading(true);
    setError(null);

    const imageData = captureScreenshot();
    if (!imageData) {
      setError('Unable to capture viewport');
      setIsLoading(false);
      return;
    }

    const prompt = `You are analyzing a 3D CAD engine assembly. Create a guided tour with exactly 5 steps explaining how this engine works. For each step, provide: a short title (5 words max) and a 2-sentence explanation. Respond ONLY in this exact JSON format, no other text: [{ "step": 1, "title": "...", "explanation": "..." }, { "step": 2, "title": "...", "explanation": "..." }, { "step": 3, "title": "...", "explanation": "..." }, { "step": 4, "title": "...", "explanation": "..." }, { "step": 5, "title": "...", "explanation": "..." }]`;

    try {
      const response = await fetch(`${BACKEND_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image: imageData,
          question: prompt,
          context: modelInfo ? `Model: ${modelInfo.name}` : ''
        })
      });

      if (!response.ok) throw new Error('API request failed');

      const data = await response.json();
      
      // Try to parse JSON from response
      try {
        // Extract JSON array from response (handle potential markdown code blocks)
        let jsonStr = data.response;
        const jsonMatch = jsonStr.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          jsonStr = jsonMatch[0];
        }
        const parsed = JSON.parse(jsonStr);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setSteps(parsed);
        } else {
          throw new Error('Invalid format');
        }
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        // Fallback: create a single step with raw response
        setSteps([{ step: 1, title: 'Analysis Result', explanation: data.response.substring(0, 300) }]);
      }
    } catch (err) {
      console.error('Tour generation failed:', err);
      setError('Failed to generate tour. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 w-[480px] max-w-[90vw]"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        data-testid="guided-tour"
      >
        <div
          className="rounded-2xl overflow-hidden shadow-2xl"
          style={{
            background: 'rgba(10, 10, 25, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-semibold text-white">✦ AI Guided Tour</span>
            </div>
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="px-5 py-4">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mb-3" />
                <p className="text-gray-400 text-sm">Generating tour...</p>
              </div>
            ) : error ? (
              <div className="text-center py-6">
                <p className="text-red-400 text-sm mb-3">{error}</p>
                <button
                  onClick={generateTour}
                  className="px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm hover:bg-cyan-500 transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : steps.length > 0 ? (
              <div>
                {/* Progress dots */}
                <div className="flex items-center justify-center gap-2 mb-4">
                  {steps.map((_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full transition-all ${
                        i === currentStep ? 'bg-cyan-400 w-4' : 'bg-white/20'
                      }`}
                    />
                  ))}
                </div>

                {/* Step content */}
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="text-center"
                >
                  <div className="text-xs text-gray-500 mb-1">Step {currentStep + 1} of {steps.length}</div>
                  <h3 className="text-lg font-bold text-cyan-400 mb-2">
                    {steps[currentStep]?.title}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {steps[currentStep]?.explanation}
                  </p>
                </motion.div>

                {/* Navigation */}
                <div className="flex items-center justify-between mt-5">
                  <button
                    onClick={handlePrev}
                    disabled={currentStep === 0}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={currentStep === steps.length - 1}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-cyan-600 text-white hover:bg-cyan-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GuidedTour;
